import React, { useMemo } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import type { Employee, EmployeePayType } from '../../types/employee';
import { resolvePayType, isDailyWorkerPay, isBusinessServicePay } from '../../types/employee';
import {
  dailyWorkerForDay,
  dailyWorkerForMonth,
  businessServiceForPayment,
} from '../../lib/payroll/withholding-helper';
import {
  estimateDayInsurance,
  workerDeductionFromNetPay,
} from '../../lib/payroll/daily-worker-insurance';
import {
  dailyWorkerRatesFromRegulatory,
  dayInsuranceRatesFromRegulatory,
  businessIncomeWithLocalFromRegulatory,
} from '../../lib/payroll/types';
import {
  useCurrentRates,
  useWithholdingDailyWorkerDailyExempt,
} from '../../hooks/useRates';
import { formatNumber } from '../../utils/formatters';
import DailyWorkerInsuranceHint from './DailyWorkerInsuranceHint';

interface EmployeePayPreviewCardProps {
  name?: string;
  payType: EmployeePayType;
  grossAmount: number;
  grossLabel?: string;
  employee?: Partial<Employee> | null;
  insuranceWorkDays?: number;
  showInsuranceHint?: boolean;
}

const Row: React.FC<{
  label: string;
  amount: number;
  tone?: 'default' | 'danger' | 'brand';
  hint?: string;
}> = ({ label, amount, tone = 'default', hint }) => {
  const color =
    tone === 'danger'
      ? 'var(--c-red-600)'
      : tone === 'brand'
        ? 'var(--primary)'
        : 'var(--fg-default)';

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', py: 0.75 }}>
      <Box>
        <Typography variant="body2" sx={{ color: 'var(--fg-secondary)' }}>
          {label}
        </Typography>
        {hint && (
          <Typography variant="caption" sx={{ color: 'var(--fg-tertiary)' }}>
            {hint}
          </Typography>
        )}
      </Box>
      <Typography className="ds-num-md" sx={{ color, fontWeight: tone === 'brand' ? 700 : 500 }}>
        {tone === 'danger' && amount > 0 ? `- ${formatNumber(amount)}` : formatNumber(amount)}원
      </Typography>
    </Box>
  );
};

const EmployeePayPreviewCard: React.FC<EmployeePayPreviewCardProps> = ({
  name,
  payType,
  grossAmount,
  grossLabel,
  employee,
  insuranceWorkDays = 1,
  showInsuranceHint = true,
}) => {
  const rates = useCurrentRates();
  const dailyExempt = useWithholdingDailyWorkerDailyExempt();

  const preview = useMemo(() => {
    if (grossAmount <= 0) return null;

    if (payType === 'businessService') {
      const combined = businessIncomeWithLocalFromRegulatory(rates);
      const { withholding, netPay } = businessServiceForPayment(grossAmount, combined);
      return { gross: grossAmount, withholding, localTax: 0, workerEmployment: withholding, netPay };
    }

    if (payType === 'dailyWorker') {
      const dailyRate = Math.round(grossAmount / Math.max(1, insuranceWorkDays));
      const dwRates = dailyWorkerRatesFromRegulatory(rates);
      const month =
        insuranceWorkDays > 1
          ? dailyWorkerForMonth(dailyRate, insuranceWorkDays, dwRates)
          : dailyWorkerForDay(dailyRate, dwRates);

      const emp = {
        payType: 'dailyWorker' as const,
        isHourly: false,
        dailyRate,
        hasFourInsurance: employee?.hasFourInsurance ?? false,
        industrialAccidentRate: employee?.industrialAccidentRate ?? 0,
        ...employee,
      };
      const estimate = estimateDayInsurance(emp as Employee, dayInsuranceRatesFromRegulatory(rates));
      const totalSalary =
        insuranceWorkDays > 1 ? dailyRate * insuranceWorkDays : grossAmount;
      const workerDeduction = workerDeductionFromNetPay(
        emp as Employee,
        insuranceWorkDays,
        totalSalary,
        estimate,
        rates,
      );

      const incomeTax = 'incomeTax' in month ? month.incomeTax : 0;
      const localTax = 'localTax' in month ? month.localTax : 0;
      const netPay = totalSalary - incomeTax - localTax - workerDeduction;

      return {
        gross: totalSalary,
        withholding: incomeTax,
        localTax,
        workerEmployment: workerDeduction,
        netPay,
        estimate,
      };
    }

    return null;
  }, [grossAmount, payType, insuranceWorkDays, employee, rates]);

  if (!preview) return null;

  const resolvedType = resolvePayType(payType, false);
  const label =
    grossLabel ??
    (resolvedType === 'dailyWorker'
      ? insuranceWorkDays > 1
        ? '일당 합계'
        : '일당'
      : resolvedType === 'businessService'
        ? '지급 합계'
        : '지급액');

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: 'var(--bg-base)',
        border: '1px solid var(--border-default)',
      }}
    >
      {name && (
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'var(--fg-strong)', mb: 1.5 }}>
          {name}
        </Typography>
      )}

      <Row label={label} amount={preview.gross} />

      {isDailyWorkerPay({ payType: resolvedType, isHourly: false }) && (
        <>
          <Row
            label="원천징수"
            amount={preview.withholding + preview.localTax}
            hint={
              preview.withholding + preview.localTax === 0
                ? `${formatNumber(dailyExempt)}원 이하 면세`
                : undefined
            }
          />
          <Row label="고용보험(근로자)" amount={preview.workerEmployment} tone="danger" />
        </>
      )}

      {isBusinessServicePay({ payType: resolvedType, isHourly: false }) && (
        <Row label="원천징수(3.3%)" amount={preview.workerEmployment} tone="danger" />
      )}

      <Divider sx={{ my: 1, borderColor: 'var(--border-subtle)' }} />
      <Row label="실수령" amount={preview.netPay} tone="brand" />

      {showInsuranceHint &&
        isDailyWorkerPay({ payType: resolvedType, isHourly: false }) &&
        preview.estimate && (
          <DailyWorkerInsuranceHint
            estimate={preview.estimate}
            workDays={insuranceWorkDays}
            compact
          />
        )}
    </Box>
  );
};

export default EmployeePayPreviewCard;
